import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  useSubmitAppFeedback,
  appFeedbackSubmitErrorMessage,
} from "@/hooks/use-app-feedback";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const MAX_LEN = 4000;

interface AppFeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Current route path + query (helps admins understand where feedback came from). */
  contextPath: string;
}

export function AppFeedbackDialog({
  open,
  onOpenChange,
  contextPath,
}: AppFeedbackDialogProps) {
  const submit = useSubmitAppFeedback();
  const [text, setText] = useState("");

  const handleClose = (next: boolean) => {
    if (!next && !submit.isPending) setText("");
    onOpenChange(next);
  };

  const handleSubmit = async () => {
    const body = text.trim();
    if (!body.length) return;
    try {
      await submit.mutateAsync({ body, contextPath });
      toast.success("Thanks — your feedback was sent to the team.");
      setText("");
      onOpenChange(false);
    } catch (err) {
      toast.error(appFeedbackSubmitErrorMessage(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Send feedback</DialogTitle>
          <DialogDescription>
            Tell us what to improve: bugs, confusing flows, missing features, or
            ideas. This goes to ME the dev.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Textarea
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, MAX_LEN))}
            placeholder="Your suggestions…"
            rows={6}
            disabled={submit.isPending}
            className="resize-y min-h-[120px]"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span className="truncate pr-2" title={contextPath}>
              Page: <span className="font-mono">{contextPath || "/"}</span>
            </span>
            <span>
              {text.length}/{MAX_LEN}
            </span>
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleClose(false)}
            disabled={submit.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={submit.isPending || !text.trim()}
          >
            {submit.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            )}
            Submit feedback
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
